import React, { useMemo, useState, useEffect, useRef } from "react";
import styled from "styled-components";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { toast, Toaster } from "react-hot-toast";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { colors } from "../../../Shared/colors";
import { decryptData } from "../../../services/crypto";
import { useGetActiveStaffQuery } from "../../../features/employeeSlice";
import {
  useGetAllActiveFormsQuery,
  useGetMetaLeadsQuery,
  useImportMetaLeadsMutation,
} from "../../../features/socialMediaSlice";
import { SocialLeadsSummary } from "./SocialLeadsSummary";
import { SocialLeadsHealth } from "./SocialLeadsHealth";
import { SocialLeadsFilters } from "./SocialLeadsFilters";
import { SocialLeadsDataTable } from "./SocialLeadsDataTable";
import { SocialLeadsVolumeChart } from "./SocialLeadsVolumeChart";
import { SocialLeadsResponseChart } from "./SocialLeadsResponseChart";
import { GhostButton } from "./socialLeadsStyles";
import { responseTone } from "./socialLeadsFormat";
import { SOCIAL_LEADS_OVERSIGHT_ROLES } from "../../../Shared/roleAccess";

const DEFAULT_RANGE_DAYS = 7;

const readRole = () => {
  const stored = localStorage.getItem("role");
  if (!stored) return null;
  try {
    return decryptData(stored);
  } catch (error) {
    return null;
  }
};

/**
 * Social Media (Meta leads) screen.
 *
 * Shows every lead form submission Meta holds for a date range alongside what
 * the CRM has, so nobody has to open Facebook to check whether an enquiry
 * came through. Anything Meta has and the CRM doesn't can be imported from
 * here, one row at a time or in bulk.
 *
 * Every role with access sees the same data and the same actions. What differs
 * is where they land: oversight roles open on everything plus the assignment
 * spread, while everyone else opens on the leads still needing import, which
 * is the working queue. Both lists live in Shared/roleAccess.js.
 *
 * Access is currently limited to "v3" for testing — see that file to open it
 * up to CEO and Admin.
 */
export const SocialLeadsFirst = () => {
  const role = useMemo(readRole, []);
  const isOversight = SOCIAL_LEADS_OVERSIGHT_ROLES.includes(role);

  const [searchParams, setSearchParams] = useSearchParams();

  const defaultFilters = useMemo(
    () => ({
      from: moment()
        .subtract(DEFAULT_RANGE_DAYS - 1, "days")
        .format("YYYY-MM-DD"),
      to: moment().format("YYYY-MM-DD"),
      formId: "",
      assignedTo: "",
      search: "",
      // Admin lands on their work queue; oversight roles see the whole picture.
      status: isOversight ? "" : "notImported",
      sort: "newest",
      // Drill-down from the response strip: "" | "overdue" | "waiting".
      // Applied here rather than server-side because the rows already carry
      // the timings it needs, and it has to survive a shared link.
      attention: "",
    }),
    [isOversight]
  );

  // Filters live in the URL so a filtered view can be shared or refreshed
  // without losing what you were looking at.
  const filtersFromUrl = useMemo(() => {
    const next = { ...defaultFilters };
    Object.keys(defaultFilters).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null) next[key] = value;
    });
    return next;
  }, [searchParams, defaultFilters]);

  const [draftFilters, setDraftFilters] = useState(filtersFromUrl);
  const [appliedFilters, setAppliedFilters] = useState(filtersFromUrl);
  const [selection, setSelection] = useState([]);
  const [pageSize, setPageSize] = useState(50);
  const tableRef = useRef(null);

  // Keep the applied filters in step when the URL changes from outside the
  // card (back button, a shared link opened in place).
  useEffect(() => {
    setAppliedFilters(filtersFromUrl);
    setDraftFilters(filtersFromUrl);
  }, [filtersFromUrl]);

  const { data: forms = [] } = useGetAllActiveFormsQuery();
  const { data: staff = [] } = useGetActiveStaffQuery();

  // `attention` is ours, not the API's — sending it would put an unrecognised
  // parameter on every request and split the RTK Query cache in two for what
  // is the same set of rows.
  const queryFilters = useMemo(() => {
    const { attention, ...rest } = appliedFilters;
    return rest;
  }, [appliedFilters]);

  const {
    data,
    isFetching,
    error,
    refetch,
  } = useGetMetaLeadsQuery(queryFilters);

  const [importMetaLeads, { isLoading: isImporting }] =
    useImportMetaLeadsMutation();

  const rows = useMemo(() => {
    const source = data?.rows || [];
    // DataGrid needs a stable `id`; the Meta leadgen id is the natural key.
    const mapped = source.map((row) => ({ ...row, id: row.leadgenId }));

    if (appliedFilters.sort === "oldest") {
      return [...mapped].sort(
        (a, b) => new Date(a.submittedAt) - new Date(b.submittedAt)
      );
    }
    if (appliedFilters.sort === "action") {
      const weight = { failed: 0, notImported: 1, inCrm: 2 };
      return [...mapped].sort(
        (a, b) =>
          (weight[a.status] ?? 3) - (weight[b.status] ?? 3) ||
          new Date(b.submittedAt) - new Date(a.submittedAt)
      );
    }
    return [...mapped].sort(
      (a, b) => new Date(b.submittedAt) - new Date(a.submittedAt)
    );
  }, [data, appliedFilters.sort]);

  const slaMinutes = data?.stats?.response?.slaMinutes;

  // What the table shows. The summary strip above keeps the full set, so its
  // tiles and the assignment spread go on describing the whole date range
  // rather than shrinking to whatever was just drilled into.
  const visibleRows = useMemo(() => {
    const { attention } = appliedFilters;
    if (!attention) return rows;

    const matching = rows.filter(
      (row) =>
        row.status === "inCrm" &&
        responseTone({
          minutesToFirstContact: row.minutesToFirstContact,
          minutesWaiting: row.minutesWaiting,
          slaMinutes,
        }) === attention
    );

    // Longest wait first: this is a call list, and the coldest lead is the one
    // to ring next. It overrides the sort dropdown while the drill-down is on.
    return matching.sort(
      (a, b) => (b.minutesWaiting ?? 0) - (a.minutesWaiting ?? 0)
    );
  }, [rows, appliedFilters, slaMinutes]);

  const handleApply = () => {
    setSelection([]);
    const params = {};
    Object.entries(draftFilters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    setSearchParams(params);
  };

  /**
   * Drill into (or back out of) the leads nobody has called yet.
   *
   * Status, assignee and search are all cleared alongside it, because the
   * number being clicked is computed server-side across the whole date range
   * and ignores those three. Leaving any of them on would show fewer rows than
   * the count that was clicked, which reads as a bug. The dates are kept —
   * they are what the figure was measured over.
   *
   * Clearing status matters most: these leads are already in the CRM, so the
   * "Not imported" tab an admin lands on would filter out every one of them.
   */
  const handleAttentionSelect = (next) => {
    setSelection([]);

    const params = {};
    Object.entries({
      ...appliedFilters,
      ...(next ? { status: "", assignedTo: "", search: "" } : {}),
      attention: next,
    }).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });
    setSearchParams(params);

    if (!next) return;
    // The strip sits above two charts and the filter card, so the table it
    // just narrowed is usually off-screen. Wait a frame so the rows are there
    // to scroll to.
    requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleImport = async (leadgenIds) => {
    if (!leadgenIds.length) return;

    // The importer needs each lead's form alongside its id — that is what
    // decides the project — so pair them up from the rows already on screen.
    const selected = new Set(leadgenIds);
    const leads = rows
      .filter((row) => selected.has(row.leadgenId))
      .map((row) => ({ leadgenId: row.leadgenId, formId: row.formId }));

    if (!leads.length) return;

    try {
      const result = await importMetaLeads(leads).unwrap();
      const created = result?.summary?.created ?? 0;
      const failed = result?.summary?.failed ?? 0;

      if (created > 0) {
        toast.success(
          created === 1 ? "1 lead imported" : `${created} leads imported`
        );
      }
      if (failed > 0) {
        toast.error(
          failed === 1
            ? "1 lead couldn't be imported — see the reason on its row"
            : `${failed} leads couldn't be imported — see the reasons on their rows`
        );
      }
      if (created === 0 && failed === 0) {
        toast("Those leads were already in the CRM");
      }
      setSelection([]);
    } catch (err) {
      toast.error("Import failed. Nothing was changed — try again.");
    }
  };

  const lastSynced = isFetching ? null : moment();

  return (
    <Main>
      <PageHead>
        <div>
          <h1>Social Media</h1>
          <p className="sub">
            Lead form submissions from Meta, matched against what is already in
            the CRM.
          </p>
        </div>
        <HeadActions>
          {!error && (
            <Live>
              <span className="dot" />
              {lastSynced ? `Synced ${lastSynced.format("HH:mm")}` : "Checking…"}
            </Live>
          )}
          <GhostButton type="button" onClick={refetch} disabled={isFetching}>
            <RefreshOutlinedIcon />
            Check Meta now
          </GhostButton>
        </HeadActions>
      </PageHead>

      <SocialLeadsHealth health={data?.stats?.health} />

      <SocialLeadsSummary
        stats={data?.stats}
        rows={rows}
        showDistribution={isOversight}
        attention={appliedFilters.attention}
        onAttentionSelect={handleAttentionSelect}
      />

      {/* Both charts read from stats, which the server computes over the whole
          window — so they describe the range being looked at rather than
          whichever status tab happens to be selected below. */}
      <Charts $single={!isOversight}>
        <SocialLeadsVolumeChart volume={data?.stats?.volume} />
        {isOversight ? (
          <SocialLeadsResponseChart
            employees={data?.stats?.responseByEmployee}
            slaMinutes={data?.stats?.response?.slaMinutes}
          />
        ) : null}
      </Charts>

      <SocialLeadsFilters
        filters={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApply}
        forms={forms}
        employees={staff}
        isFetching={isFetching}
      />

      <div ref={tableRef}>
        <SocialLeadsDataTable
          rows={visibleRows}
          isFetching={isFetching}
          error={error}
          selection={selection}
          onSelectionChange={setSelection}
          onImport={handleImport}
          isImporting={isImporting}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          // The response threshold is defined once, server-side, so the table and
          // the summary strip can never disagree about what counts as late.
          slaMinutes={data?.stats?.response?.slaMinutes}
          attention={appliedFilters.attention}
          onClearAttention={() => handleAttentionSelect("")}
        />
      </div>

      <Toaster />
    </Main>
  );
};

const Main = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
  padding: 1.2rem 1.4rem 3rem 1.4rem;
`;

/* Side by side on a wide screen, stacked below it. The response chart grows
   with the number of employees, so the two are not forced to equal height. */
const Charts = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.$single ? "1fr" : "1fr 1fr")};
  /* Stretch, so both cards end on the same line. Each one fills the row and
     caps its own plot, rather than the response chart growing a row taller
     with every employee and stranding the volume chart in whitespace. */
  align-items: stretch;
  gap: 0.9rem;
  margin-bottom: 1rem;

  /* Grid items default to min-width:auto, which means they refuse to shrink
     below their content and overflow the row instead. The charts size
     themselves from their container, so without this they can only ever grow. */
  > * {
    min-width: 0;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const PageHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.1rem;

  h1 {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.2px;
    color: ${colors.text};
  }

  .sub {
    margin: 3px 0 0 0;
    font-size: 0.8rem;
    color: ${colors.textMed};
  }
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Live = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.76rem;
  color: ${colors.textMed};
  background: ${colors.white};
  border: 1px solid ${colors.inputBorderWhite};
  padding: 7px 13px;
  min-height: 34px;
  border-radius: 20px;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
  }
`;
